import { FC, KeyboardEvent, useState } from 'react';
import { Input, Button } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';

import { useFelaEnhanced } from 'hooks';

import * as felaRules from './AskInput.rules';
import { EXAMPLE_QUESTION_IDS } from '../../constants';

interface AskInputProps {
    loading: boolean;
    showExamples: boolean;
    onSubmit: (question: string) => void;
}

const AskInput: FC<AskInputProps> = ({ loading, showExamples, onSubmit }) => {
    const { styles } = useFelaEnhanced(felaRules);
    const intl = useIntl();
    const [value, setValue] = useState('');

    const handleSubmit = () => {
        const trimmed = value.trim();
        if (!trimmed || loading) {
            return;
        }
        onSubmit(trimmed);
        setValue('');
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div>
            <div className={styles.container}>
                <Input.TextArea
                    className={styles.textarea}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    placeholder={intl.formatMessage({ id: 'assistant.input.placeholder' })}
                    value={value}
                    onChange={event => setValue(event.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                />
                <div className={styles.footer}>
                    <Button
                        className={styles.submitButton}
                        type="primary"
                        icon={<SendOutlined />}
                        loading={loading}
                        disabled={!value.trim()}
                        onClick={handleSubmit}
                    >
                        {intl.formatMessage({ id: 'assistant.input.submit' })}
                    </Button>
                </div>
            </div>
            {showExamples && (
                <div className={styles.examples}>
                    {EXAMPLE_QUESTION_IDS.map(id => {
                        const question = intl.formatMessage({ id });
                        return (
                            <div
                                key={id}
                                className={styles.exampleChip}
                                onClick={() => !loading && onSubmit(question)}
                            >
                                {question}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AskInput;
